import { Editor } from '../core/Editor';
import { ProjectStorage } from '../core/ProjectStorage';
import { Icons } from './Icons';

export class StartScreen {
  private editor: Editor;
  private parentContainer: HTMLElement;
  public element: HTMLElement;
  private hiddenFileInput: HTMLInputElement;

  // Canvas Mode: 'auto' locks to 1st photo dimensions, 'preset' locks to chosen background dimensions
  private canvasMode: 'auto' | 'preset' = 'auto';
  private targetW: number = 1080;
  private targetH: number = 1920;

  constructor(editor: Editor, parentContainer: HTMLElement) {
    this.editor = editor;
    this.parentContainer = parentContainer;

    // Create file input for opening photos
    this.hiddenFileInput = document.createElement('input');
    this.hiddenFileInput.type = 'file';
    this.hiddenFileInput.accept = 'image/*,.photodrb';
    this.hiddenFileInput.style.display = 'none';
    this.hiddenFileInput.id = 'start-screen-file-input';
    document.body.appendChild(this.hiddenFileInput);

    this.hiddenFileInput.addEventListener('change', () => {
      this.handleStartFiles(this.hiddenFileInput.files);
      this.hiddenFileInput.value = '';
    });

    this.element = document.createElement('div');
    this.element.className = 'start-screen';
    this.element.id = 'start-screen';

    this.parentContainer.appendChild(this.element);
    this.render();
    this.attachEvents();
    this.checkSavedProject();
  }

  public render() {
    this.element.innerHTML = `
      <div class="start-card">
        <!-- Header / Logo -->
        <div class="start-header">
          <div class="start-badge">
            <span class="start-badge-dot"></span>
            <span>Iron Crop Pro Photo Editor</span>
          </div>
          <h1 class="start-title">Start a New Creation</h1>
          <p class="start-subtitle">
            Drop any photo onto the screen to lock canvas to its exact dimensions, or choose your background size first so dragging photos never changes your canvas size.
          </p>
        </div>

        <!-- Saved Project Resume Banner (shown if an active project exists in storage) -->
        <div class="start-resume-card" id="start-resume-card" style="display: none;">
          <div class="resume-left">
            <span class="resume-badge">💾 AUTOSAVED SESSION IN STORAGE</span>
            <div class="resume-title" id="resume-project-title">Auto-Saved Project</div>
            <div class="resume-sub" id="resume-project-sub">Click Resume to continue where you left off, or Start New to clear storage</div>
          </div>
          <div class="resume-actions">
            <button type="button" class="resume-btn-primary" id="start-resume-btn">
              <span>▶️ Resume Project</span>
            </button>
            <button type="button" class="resume-btn-danger" id="start-clear-btn" title="Clear this project from local storage and start fresh">
              <span>🗑️ Clear & Start New</span>
            </button>
          </div>
        </div>

        <!-- Canvas Background Sizing Mode Selector -->
        <div class="start-mode-selector">
          <div class="mode-selector-header">
            <span class="mode-label-title">🎯 Canvas Background Mode:</span>
            <span class="mode-label-hint" id="mode-hint-text">1st photo defines & locks the canvas size</span>
          </div>
          <div class="mode-pills" id="start-mode-pills">
            <button type="button" class="mode-pill ${this.canvasMode === 'auto' ? 'active' : ''}" data-mode="auto" title="Drop photo to lock canvas to its natural dimensions">
              <span class="mode-pill-icon">🔒</span>
              <span class="mode-pill-text">Auto-Lock to 1st Photo</span>
            </button>
            <button type="button" class="mode-pill ${this.canvasMode === 'preset' && this.targetW === 1080 && this.targetH === 1920 ? 'active' : ''}" data-mode="preset" data-w="1080" data-h="1920" title="Lock background to 1080 × 1920 (Instagram Reels, TikTok & 360 Photobooths)">
              <span class="mode-pill-icon">📱</span>
              <span class="mode-pill-text">1080 × 1920 (Reel / Booth)</span>
            </button>
            <button type="button" class="mode-pill" data-mode="preset" data-w="1920" data-h="1080" title="Lock background to 1920 × 1080 Landscape Full HD">
              <span class="mode-pill-icon">🖥️</span>
              <span class="mode-pill-text">1920 × 1080 (HD)</span>
            </button>
            <button type="button" class="mode-pill" data-mode="preset" data-w="1080" data-h="1080" title="Lock background to 1080 × 1080 Square Post">
              <span class="mode-pill-icon">🔲</span>
              <span class="mode-pill-text">1080 × 1080 (Square)</span>
            </button>
            <button type="button" class="mode-pill" data-mode="preset" data-w="1200" data-h="1800" title="Lock background to 4 × 6 inches @ 300 DPI High-Res Print">
              <span class="mode-pill-icon">🖼️</span>
              <span class="mode-pill-text">4 × 6 in (Print 300 DPI)</span>
            </button>
          </div>
        </div>

        <!-- Interactive Drag & Drop Hero Zone -->
        <div class="start-dropzone" id="start-dropzone" title="Click to browse files or drag and drop an image here">
          <div class="start-drop-icon">
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </div>
          <div class="start-drop-content">
            <span class="start-drop-headline" id="start-drop-headline">Drop your photo here</span>
            <span class="start-drop-sub" id="start-drop-sub">Canvas will automatically match and lock to the photo's exact dimensions</span>
          </div>
          <div class="start-drop-formats">
            <span class="fmt-pill">PNG</span>
            <span class="fmt-pill">JPG</span>
            <span class="fmt-pill">WEBP</span>
            <span class="fmt-pill">AVIF</span>
            <span class="fmt-pill">SVG</span>
            <span class="fmt-pill">.photodrb</span>
          </div>
        </div>

        <!-- Primary 3 Options Grid -->
        <div class="start-primary-grid">
          <!-- Option 1: Make Photo Frame (Featured!) -->
          <div class="primary-action-card featured" id="start-make-frame-card" title="Create Photo Frame: Portrait (1080×1920) -> Landscape (1920×1080) with moveable logo & 7s audio reel">
            <div class="action-card-header">
              <span class="card-tag gold">FEATURED • 4-STEP STUDIO</span>
              <span class="card-icon-big">🎞️</span>
            </div>
            <div class="action-card-body">
              <h3 class="action-card-title">Make Photo Frame</h3>
              <p class="action-card-desc">
                1. Make Portrait (1080×1920) &rarr; 2. Copy & move logo on Landscape (1920×1080) &rarr; 3. Pick 7s audio &rarr; 4. ZIP Package!
              </p>
            </div>
            <button type="button" class="action-card-btn primary">
              <span>Start Frame Studio</span>
              <span class="arrow">&rarr;</span>
            </button>
          </div>

          <!-- Option 2: Open Photo -->
          <div class="primary-action-card" id="start-open-card" title="Open an image file and lock canvas to its size">
            <div class="action-card-header">
              <span class="card-tag">LOCK CANVAS</span>
              <span class="card-icon-big">📸</span>
            </div>
            <div class="action-card-body">
              <h3 class="action-card-title">Open Photo</h3>
              <p class="action-card-desc">
                Load any image and automatically lock the background to its exact dimensions.
              </p>
            </div>
            <button type="button" class="action-card-btn secondary">
              <span>Open Image...</span>
              <kbd class="start-kbd">Ctrl+O</kbd>
            </button>
          </div>

          <!-- Option 3: Create Custom Canvas -->
          <div class="primary-action-card" id="start-new-card" title="Create a blank canvas in pixels or inches">
            <div class="action-card-header">
              <span class="card-tag">CUSTOM SIZING</span>
              <span class="card-icon-big">📐</span>
            </div>
            <div class="action-card-body">
              <h3 class="action-card-title">Blank Canvas</h3>
              <p class="action-card-desc">
                Choose custom canvas size in px or inches (300 DPI print) with white or transparent background.
              </p>
            </div>
            <button type="button" class="action-card-btn secondary">
              <span>New Canvas...</span>
              <kbd class="start-kbd">Ctrl+N</kbd>
            </button>
          </div>
        </div>

        <!-- Quick Size Presets Bar -->
        <div class="start-presets-section">
          <div class="start-presets-label">Click to Start with Blank Locked Canvas:</div>
          <div class="start-preset-chips">
            <button type="button" class="preset-chip" data-w="1920" data-h="1080" data-bg="#ffffff" title="1920 × 1080 px Full HD">
              <span class="chip-name">1920 × 1080</span>
              <span class="chip-tag">Full HD</span>
            </button>
            <button type="button" class="preset-chip" data-w="1080" data-h="1080" data-bg="#ffffff" title="1080 × 1080 px Square">
              <span class="chip-name">1080 × 1080</span>
              <span class="chip-tag">Square</span>
            </button>
            <button type="button" class="preset-chip" data-w="1080" data-h="1920" data-bg="#ffffff" title="1080 × 1920 px Reel / Story">
              <span class="chip-name">1080 × 1920</span>
              <span class="chip-tag">Story</span>
            </button>
            <button type="button" class="preset-chip" data-w="1200" data-h="1800" data-bg="#ffffff" title="4 × 6 inches @ 300 DPI Print Photo">
              <span class="chip-name">4 × 6 in</span>
              <span class="chip-tag">300 DPI</span>
            </button>
            <button type="button" class="preset-chip" data-w="2550" data-h="3300" data-bg="#ffffff" title="8.5 × 11 inches @ 300 DPI US Letter">
              <span class="chip-name">8.5 × 11 in</span>
              <span class="chip-tag">Letter</span>
            </button>
          </div>
        </div>

        <!-- Sample Demos Footer -->
        <div class="start-samples-row">
          <span class="samples-title">Or test with a sample:</span>
          <button type="button" class="sample-link" id="sample-sneaker">👟 Cyber Sneaker</button>
          <span class="sample-sep">•</span>
          <button type="button" class="sample-link" id="sample-portrait">👤 Studio Portrait</button>
        </div>
      </div>
    `;
  }

  public async checkSavedProject() {
    try {
      const hasSaved = await ProjectStorage.hasSaved();
      const resumeCard = this.element.querySelector('#start-resume-card') as HTMLElement;
      if (resumeCard) {
        if (hasSaved) {
          const proj = await ProjectStorage.load();
          if (proj) {
            const title = this.element.querySelector('#resume-project-title');
            const sub = this.element.querySelector('#resume-project-sub');
            if (title) title.textContent = `Auto-Saved Project (${proj.width} × ${proj.height} px)`;
            if (sub) sub.textContent = `${proj.layers.length} layers • Last edited ${new Date(proj.timestamp).toLocaleTimeString()}`;
          }
          resumeCard.style.display = 'flex';
        } else {
          resumeCard.style.display = 'none';
        }
      }
    } catch (err) {
      console.warn('Could not check saved project in storage:', err);
    }
  }

  private updateDropzoneText() {
    const headline = this.element.querySelector('#start-drop-headline');
    const sub = this.element.querySelector('#start-drop-sub');
    const hint = this.element.querySelector('#mode-hint-text');

    if (this.canvasMode === 'auto') {
      if (headline) headline.textContent = 'Drop your photo here';
      if (sub) sub.textContent = "Canvas will automatically match and lock to the photo's exact dimensions";
      if (hint) hint.textContent = '1st photo defines & locks the canvas size';
    } else {
      if (headline) headline.textContent = `Drop photo to place onto ${this.targetW} × ${this.targetH} canvas`;
      if (sub) sub.textContent = `Background size is locked to ${this.targetW} × ${this.targetH} px • Photo will fit as a layer without resizing canvas`;
      if (hint) hint.textContent = `Canvas locked to ${this.targetW} × ${this.targetH} px`;
    }
  }

  private async handleStartFiles(files: FileList | null | undefined) {
    if (!files || files.length === 0) return;
    const fileList = Array.from(files);

    const proj = fileList.find(f => f.name.endsWith('.photodrb'));
    if (proj) {
      this.editor.loadProjectFromFile(proj);
      return;
    }

    const imageFiles = fileList.filter(f => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;

    if (this.canvasMode === 'auto') {
      // 1st photo locks canvas size to its natural dimensions:
      try {
        await this.editor.openImageFileAsync(imageFiles[0]);
        // Any remaining files are added as layers onto that locked canvas:
        for (let i = 1; i < imageFiles.length; i++) {
          await this.editor.importImageAsLayerAsync(imageFiles[i], { fitToCanvas: true });
        }
      } catch (err) {
        console.error('Error opening photo:', err);
      }
    } else {
      // Background size is pre-selected (e.g. 1080x1920):
      // Initialize canvas at chosen size with locked background:
      this.editor.initNewCanvas(this.targetW, this.targetH, '#ffffff');
      // Place dropped photos onto this locked canvas as layers (fitted proportionally):
      for (let i = 0; i < imageFiles.length; i++) {
        try {
          await this.editor.importImageAsLayerAsync(imageFiles[i], { fitToCanvas: true });
        } catch (err) {
          console.error('Error importing photo layer:', err);
        }
      }
    }
  }

  private attachEvents() {
    // Resume Saved Project button
    this.element.querySelector('#start-resume-btn')?.addEventListener('click', () => {
      this.editor.checkAndRestoreAutoSavedProject();
    });

    // Clear Saved Project & Start New button
    this.element.querySelector('#start-clear-btn')?.addEventListener('click', () => {
      this.editor.startNew(false);
      this.checkSavedProject();
    });

    // Mode pills toggle
    this.element.querySelectorAll('.mode-pill').forEach(pill => {
      pill.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const mode = target.dataset.mode as 'auto' | 'preset';
        this.element.querySelectorAll('.mode-pill').forEach(p => p.classList.remove('active'));
        target.classList.add('active');

        this.canvasMode = mode;
        if (mode === 'preset') {
          this.targetW = parseInt(target.dataset.w || '1080');
          this.targetH = parseInt(target.dataset.h || '1920');
        }
        this.updateDropzoneText();
      });
    });

    // Primary Grid Action Cards
    this.element.querySelector('#start-make-frame-card')?.addEventListener('click', () => {
      this.editor.openOverlayStudioModal();
    });

    this.element.querySelector('#start-open-card')?.addEventListener('click', () => {
      this.triggerFileOpen();
    });

    this.element.querySelector('#start-new-card')?.addEventListener('click', () => {
      this.editor.openNewCanvasModal();
    });

    // Clicking dropzone triggers file picker
    this.element.querySelector('#start-dropzone')?.addEventListener('click', () => {
      this.triggerFileOpen();
    });

    // Preset chips (directly opens canvas with locked size)
    this.element.querySelectorAll('.preset-chip').forEach(chip => {
      chip.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const w = parseInt(target.dataset.w || '1920');
        const h = parseInt(target.dataset.h || '1080');
        const bg = target.dataset.bg || '#ffffff';
        this.editor.initNewCanvas(w, h, bg);
      });
    });

    // Sample links
    this.element.querySelector('#sample-sneaker')?.addEventListener('click', () => {
      this.editor.openSampleImage('/sample-product.jpg', 'Cyber Sneaker');
    });

    this.element.querySelector('#sample-portrait')?.addEventListener('click', () => {
      this.editor.openSampleImage('/sample-portrait.jpg', 'Studio Portrait');
    });

    // Dropzone drag feedback and event handling
    const dropzone = this.element.querySelector('#start-dropzone');
    if (dropzone) {
      dropzone.addEventListener('dragover', (e) => {
        e.preventDefault();
        dropzone.classList.add('active');
      });
      dropzone.addEventListener('dragleave', () => {
        dropzone.classList.remove('active');
      });
      dropzone.addEventListener('drop', (e: any) => {
        e.preventDefault();
        e.stopPropagation(); // Stop propagation so window drop listener doesn't duplicate
        dropzone.classList.remove('active');
        this.handleStartFiles(e.dataTransfer?.files);
      });
    }
  }

  public triggerFileOpen() {
    this.hiddenFileInput.click();
  }

  public show() {
    this.element.style.display = 'flex';
    this.checkSavedProject();
  }

  public hide() {
    this.element.style.display = 'none';
  }
}
